import React from 'react';
import { Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { formatterCurrency } from '../../../utils/methods';
import { RowActionButton, PTMemberLink, PTStack, MemberNameText, StatusChip, PTRow, PTCell, PTExpandIcon } from './SalesManagTab1Styled';
import { SPLIT_METHOD_LABEL } from '../modal/enterSaleInfoModal/SplitPaymentSection';
import SellerNameCell from '../../common/SellerNameCell';

const num = value => formatterCurrency(value || 0);
const refundText = value => (value ? `- ${formatterCurrency(Math.abs(value))}` : '-');
// 차감 항목(할인/미수금): 0이 아니면 '-' 부호를 붙이고, 0이면 그냥 0으로 표기.
const minusNum = value => (value ? `- ${formatterCurrency(Math.abs(value))}` : '0');

// 결제수단별 금액 분리(대시보드/엑셀과 동일 기준: 현금/계좌이체/카드).
const isCashMethod = m => {
  const u = (m || '').toUpperCase();
  return u === 'CASH' || u.includes('현금');
};
const isTransferMethod = m => {
  const u = (m || '').toUpperCase();
  return u.includes('계좌') || u.includes('TRANSFER');
};
const isCardMethod = m => {
  const u = (m || '').toUpperCase();
  return u === 'CARD' || u.includes('카드') || u.includes('CREDIT');
};

// 분할결제(#49) 복합 주문. 결제수단 컬럼은 'MIXED' 로 내려오고 수단별 금액은 수납 라인에 있다.
const MIXED_METHOD = 'MIXED';
// enum 원문(CARD/CASH/TRANSFER)으로 저장된 주문도 한글 라벨로 표시한다 (저장 정규화는 #76).
const METHOD_ENUM_LABELS = { MIXED: '분할결제', CARD: '카드', CASH: '현금', TRANSFER: '계좌이체' };
const paymentMethodLabel = m => METHOD_ENUM_LABELS[m] || m;

// 수단별 금액. 백엔드가 수납 라인 기준으로 분해한 값(cardAmount 등)을 내려주면 그것을 쓰고,
// 내려오지 않는 응답(구버전 백엔드)에서는 기존처럼 결제수단으로 판별한다.
const methodAmount = (row, serverValue, matches) => {
  if (serverValue !== undefined && serverValue !== null) return Number(serverValue);
  return matches(row.paymentMethod) ? row.finalPrice : 0;
};

// 환불행의 수단별 환불금액(#49). 복합 환불은 백엔드 분해값(cardRefundAmount 등), 폴백은 환불 수단 판별.
const refundMethodAmount = (row, serverValue, matches) => {
  if (serverValue !== undefined && serverValue !== null) return Number(serverValue);
  return matches(row.refundMethod) ? row.refundAmount : 0;
};

// 수단별 컬럼 공용 렌더 — 결제행은 수납 금액, 환불행은 환불 분해액을 차감 표기로.
const methodCell = (row, payValue, refundValue, matches) =>
  isRefundRow(row) ? refundText(refundMethodAmount(row, refundValue, matches)) : num(methodAmount(row, payValue, matches));

// 양수금(양도 수수료) 행 판별 — payTypeName='양수금' (payType='TRANSFER_FEE').
const TRANSFER_FEE_PAY_TYPE = 'TRANSFER_FEE';
const isTransferFeeRow = row => row.payType === TRANSFER_FEE_PAY_TYPE || row.payTypeName === '양수금';

// 환불 행 판별 — 이원화(#38)로 환불 건은 결제행(PAY)/환불행(REFUND) 2행으로 분화된다.
// 백엔드가 환불행에는 paymentStatusName='환불'을 내려주므로 그것을 1순위로 본다.
const isRefundRow = row => row.rowType === 'REFUND' || row.paymentStatusName === '환불';

// ── 복합 결제 갈매기 펼침(#49, 전체 내역 탭 전용) ──
// 결제행은 paymentMethod, 환불행은 refundMethod 가 'MIXED' 일 때만 펼칠 수 있다.
export const isMixedSplitRow = row =>
  isRefundRow(row) ? row.refundMethod === MIXED_METHOD : row.paymentMethod === MIXED_METHOD;
export const splitRowKey = row => `${row.rowType || 'PAY'}-${row.memberOrderId}`;

// 라인 상세 텍스트 — 카드는 카드사(끝번호)·승인번호, 계좌이체는 은행·입금자.
const splitLineDetailText = line => {
  const parts = [];
  if (line.cardCompany) parts.push(line.cardNumber ? `${line.cardCompany}(${line.cardNumber})` : line.cardCompany);
  // Jackson 이 cId 필드를 'cid' 키로 직렬화한다 ('cId' 키 아님)
  const approval = line.cid || line.cId;
  if (approval) parts.push(`승인번호 ${approval}`);
  if (line.bankInfo) parts.push(line.bankInfo);
  if (line.depositorName) parts.push(line.depositorName);
  return parts.join(' · ') || '-';
};

// 라인 일시 표기 — 결제 라인은 PAYMENT_DATE, 환불 라인은 부모 환불행의 일시를 따른다.
const splitLineDateText = (line, row) => {
  const raw = line.paymentDate || row.paidDate || '';
  return String(raw).replace('T', ' ').slice(0, 16);
};

// 펼침 하위 행 — 부모(전체 내역) 컬럼 폭에 1:1 정렬해 라인 값을 해당 컬럼 자리에 채운다.
// 라인에 없는 정보(판매가·할인 등)는 '-' 로 두고, 계좌이체 라인의 은행/입금자는 카드사/카드번호 자리에 표기한다.
export const renderSplitLineDetail = (lines, row, helpers) => {
  if (!lines || lines.length === 0) {
    return (
      <PTRow variant="detail">
        <PTCell flex>수납 라인이 없습니다 (백필 전 데이터).</PTCell>
      </PTRow>
    );
  }
  const refund = isRefundRow(row);
  // 부모 행과 구분되도록 하위 라인은 한 단계 진한 배경, 묶음 외곽은 진한 테두리로 한 덩어리로 감싼다.
  // 색 계열은 부모 톤을 따른다 — 결제(청록) / 환불(붉은 기, 부모 #fdf3f1 과 같은 계열).
  // 부모는 일반 행과 같은 배경 — 자식 라인 색으로 분할 묶음을 구분한다
  const childBg = refund ? '#F9E4E1' : '#E3F2F4';
  const edgeColor = refund ? '#E08A80' : '#4AB3BC';
  return lines.map((line, i) => {
    const isLast = i === lines.length - 1;
    const childRowStyle = {
      backgroundColor: childBg,
      boxShadow: `inset 2px 0 0 ${edgeColor}, inset -2px 0 0 ${edgeColor}${isLast ? `, inset 0 -2px 0 ${edgeColor}` : ''}`,
    };
    const methodName = line.paymentMethod || line.refundMethod;
    const methodText = SPLIT_METHOD_LABEL[methodName] || methodName;
    const approval = line.cid || line.cId;
    return (
      <PTRow variant="detail" style={childRowStyle} key={`${splitRowKey(row)}-line-${i}`}>
        <PTCell cellWidth="220px" nowrap>{row.branchName}</PTCell>
        <PTCell cellWidth="130px">{splitLineDateText(line, row)}</PTCell>
        <PTCell cellWidth="100px">{row.memberName || '-'}</PTCell>
        <PTCell cellWidth="112px">-</PTCell>
        <PTCell cellWidth="100px">{row.payTypeName}</PTCell>
        <PTCell cellWidth="92px">-</PTCell>
        <PTCell cellWidth="200px" align="left" nowrap>{row.courseTicketName}</PTCell>
        <PTCell cellWidth="120px" align="right">-</PTCell>
        <PTCell cellWidth="120px" align="right">-</PTCell>
        <PTCell cellWidth="120px" align="right">-</PTCell>
        <PTCell cellWidth="120px" align="right">-</PTCell>
        <PTCell cellWidth="130px" align="right">{refund ? '-' : num(line.amount)}</PTCell>
        <PTCell cellWidth="110px">{refund ? methodText : '-'}</PTCell>
        <PTCell cellWidth="120px" align="right" tone={refund ? 'refund' : undefined}>
          {refund ? refundText(line.amount) : num(0)}
        </PTCell>
        <PTCell cellWidth="120px" align="right">-</PTCell>
        <PTCell cellWidth="100px">{helpers ? statusChip(row, helpers) : '-'}</PTCell>
        <PTCell cellWidth="110px" />
        <PTCell cellWidth="100px">{refund ? '-' : methodText}</PTCell>
        <PTCell cellWidth="110px" align="right">
          {isCashMethod(methodName) ? (refund ? refundText(line.amount) : num(line.amount)) : '-'}
        </PTCell>
        <PTCell cellWidth="110px" align="right">
          {isTransferMethod(methodName) ? (refund ? refundText(line.amount) : num(line.amount)) : '-'}
        </PTCell>
        <PTCell cellWidth="110px" align="right">
          {isCardMethod(methodName) ? (refund ? refundText(line.amount) : num(line.amount)) : '-'}
        </PTCell>
        <PTCell cellWidth="130px" nowrap>{line.cardCompany || line.bankInfo || '-'}</PTCell>
        <PTCell cellWidth="150px">{line.cardNumber || line.depositorName || '-'}</PTCell>
        <PTCell cellWidth="110px">{approval || '-'}</PTCell>
        <PTCell cellWidth="100px">{row.paymentProviderName || '-'}</PTCell>
        <PTCell cellWidth="110px"><SellerNameCell managerName={row.managerName} /></PTCell>
        <PTCell cellWidth="140px" align="left">-</PTCell>
        <PTCell cellWidth="90px" />
      </PTRow>
    );
  });
};

// 결제일 셀 — 분할결제(펼침 가능) 행이면 날짜 옆에 갈매기 토글을 붙인다(수단 셀은 일반 텍스트) — 사용자 결정.
const paidDateWithChevron = (row, h) => {
  const exp = h.expansion;
  if (!exp || !exp.isExpandable(row)) return row.paidDate;
  const expanded = exp.expandedKey === exp.getKey(row);
  // 갈매기는 날짜 오른쪽에 절대배치 — 날짜 시작 위치가 일반 행과 동일하게 유지된다.
  return (
    <PTExpandIcon
      onClick={e => {
        e.stopPropagation(); // 행 전체 클릭 토글과 이중 발화 방지
        exp.toggle(row);
      }}
      sx={{ cursor: 'pointer', justifyContent: 'center', position: 'relative', display: 'inline-flex' }}>
      <span style={{ color: '#111' }}>
        {row.paidDate}
        {expanded ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 16, color: '#4ab3bc', verticalAlign: 'text-bottom', ml: '2px' }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#4ab3bc', verticalAlign: 'text-bottom', ml: '2px' }} />
        )}
      </span>
    </PTExpandIcon>
  );
};

// 행 배경 톤 — 환불이 미수금보다 강한 신호라 우선한다.
const salesRowTone = row => {
  if (isRefundRow(row)) return 'refund';
  if (Number(row.outstandingAmount) > 0) return 'outstanding';
  return undefined;
};

// 결제 상태 칩. 백엔드 paymentStatusName(한글)을 그대로 쓰되 색만 상태별로 나눈다.
// 2026-08-03 백엔드에서 REFUNDED 결제행 라벨이 '결제(환불됨)' → '결제' 로 변경됨
// (환불은 별도 행으로 이미 분리돼 있어 접미사가 중복이었음).
const STATUS_CHIP_TONE = {
  환불: 'refund',
  미수금결제: 'outstanding',
  변경결제: 'paid', // 변경결제도 일반 결제와 같은 표시(사용자 결정) — 구버전 응답 라벨 대비 매핑만 유지
  취소: 'canceled',
  결제: 'paid',
};
const statusChip = (row, helpers) => {
  // 변경결제는 일반 결제와 같은 라벨로 보여준다(사용자 결정) — 구버전 응답 라벨 정규화.
  const name = row.paymentStatusName === '변경결제' ? '결제' : row.paymentStatusName;
  if (!name || name === '-') return '-';
  // 상태는 '결제'인데 미수 잔액이 남은 행은 미수금 톤으로 보여준다.
  const tone = STATUS_CHIP_TONE[name] || (Number(row.outstandingAmount) > 0 ? 'outstanding' : 'paid');
  return <StatusChip chipTone={tone}>{helpers.t(name)}</StatusChip>;
};

// 금액 컬럼 헤더 옆 info 아이콘 툴팁 — 계산식 노출 (그룹/평면 테이블 공용).
export const COLUMN_FORMULAS = {
  판매가격: '상품 정가 합계',
  할인금액: '할인 합계(차감)',
  포인트사용: '포인트 사용 합계(차감)',
  미수금: '미수(미납) 합계(차감)',
  결제금액: '판매가격 − 할인금액 − 미수금',
  환불금액: '환불 합계(차감)',
  합계금액: '결제금액 − 환불금액 (순매출)',
};

const memberLink = (row, helpers) => (
  <PTMemberLink clickable={!!row.memberId} onClick={() => row.memberId && helpers.onNavigateMember(row.memberId)}>
    {row.memberName || '(삭제회원)'}
  </PTMemberLink>
);

// ── 그룹형 테이블 config (상품별 / 상품구분별 / 종목별 / 결제수단별) ──
// 집계는 백엔드 /payments/summary 가 전체기간 기준으로 수행한다(클라 그룹핑 없음).
// 각 그룹 행은 { groupKey, count, regular, paid, discount, usedPoint, outstanding, cash, card, refund, total }.
// 탭별 차이는 첫 컬럼(그룹키)의 헤더 라벨뿐.
export const GROUPED_CONFIGS = {
  '상품별 내역': { groupLabel: '상품명' },
  '상품구분별 내역': { groupLabel: '상품구분' },
  '종목별 내역': { groupLabel: '종목구분' },
  '결제수단별 내역': { groupLabel: '결제수단' },
};

// 그룹 탭 → 백엔드 groupBy 코드 (그룹 펼침 detail 조회에 사용)
export const TAB_TO_GROUPBY = {
  '상품별 내역': 'PRODUCT',
  '상품구분별 내역': 'PRODUCT_TYPE',
  '종목별 내역': 'SPORT_TYPE',
  '결제수단별 내역': 'PAYMENT_METHOD',
};

// ── 평면 테이블 config (환불 내역 / 미수금 내역) ──
// 서버가 filterType(REFUND/OUTSTANDING)으로 이미 필터·페이징하므로 클라 filter 없음.
export const REFUND_CONFIG = {
  tableWidth: '1552px',
  rowTone: salesRowTone,
  columns: [
    {
      label: '환불 일시',
      width: 160,
      render: row => {
        const [date, time] = (row.paidDate || '').split(' ');
        return (
          <PTStack>
            <span>{date}</span>
            {time && <span>{time}</span>}
          </PTStack>
        );
      },
    },
    { label: '회원명', width: 88, render: memberLink },
    { label: '상품구분', width: 100, render: row => row.payTypeName },
    { label: '상품수량', width: 64, render: () => 1 },
    { label: '상품명', width: 240, render: row => row.courseTicketName },
    { label: '결제상태', width: 88, render: (row, h) => statusChip(row, h) },
    { label: '환불수단', width: 88, render: (row, h) => h.t(paymentMethodLabel(row.refundMethod || row.paymentMethod)) },
    { label: '환불금액', width: 200, align: 'right', tone: 'refund', render: row => refundText(row.refundAmount) },
    { label: '판매담당자', width: 120, render: row => <SellerNameCell managerName={row.managerName} fallback="" /> },
    {
      label: '보기',
      width: 80,
      render: (row, h) => (
        <RowActionButton tone="gray" variant="contained" size="small" onClick={() => h.onClickDetail(row, 'refund')}>
          보기
        </RowActionButton>
      ),
    },
    { label: '메모', flex: true, align: 'left', render: row => row.memo },
  ],
};

export const OUTSTANDING_CONFIG = {
  tableWidth: '1996px',
  rowTone: salesRowTone,
  columns: [
    { label: '판매일', width: 160, render: row => row.paidDate },
    { label: '회원명', width: 88, render: memberLink },
    { label: '상품구분', width: 100, render: row => row.payTypeName },
    { label: '상품수량', width: 64, render: () => 1 },
    { label: '상품명', width: 240, render: row => row.courseTicketName },
    { label: '판매가격', width: 140, align: 'right', render: row => num(row.regularPrice) },
    {
      label: '할인금액',
      width: 140,
      align: 'right',
      tone: row => (row.discountPrice ? 'minus' : undefined),
      render: row => minusNum(row.discountPrice),
    },
    {
      label: '포인트사용',
      width: 140,
      align: 'right',
      tone: row => (row.usedPoint ? 'minus' : undefined),
      render: row => minusNum(row.usedPoint),
    },
    {
      label: '미수금',
      width: 140,
      align: 'right',
      tone: row => (row.outstandingAmount ? 'minus' : undefined),
      render: row => minusNum(row.outstandingAmount),
    },
    { label: '결제금액', width: 140, align: 'right', render: row => num(row.finalPrice) },
    { label: '결제상태', width: 88, render: (row, h) => statusChip(row, h) },
    { label: '결제수단', width: 88, render: (row, h) => h.t(paymentMethodLabel(row.paymentMethod)) },
    { label: '판매담당자', width: 100, render: row => <SellerNameCell managerName={row.managerName} fallback="" /> },
    {
      label: '결제변경',
      width: 88,
      render: (row, h) => (
        <Tooltip title={!h.canContractEdit ? '권한이 없습니다' : ''} arrow componentsProps={{ tooltip: { sx: { fontSize: '13px' } } }}>
          <span>
            <RowActionButton
              tone="dark"
              variant="contained"
              size="small"
              onClick={() => h.canContractEdit && h.onClickCancel(row)}
              disabled={!h.canContractEdit}
              style={!h.canContractEdit ? { opacity: 0.4, cursor: 'default' } : {}}>
              변경
            </RowActionButton>
          </span>
        </Tooltip>
      ),
    },
    {
      label: '수정',
      width: 80,
      render: (row, h) => (
        <Tooltip title={!h.canPaymentDetailEdit ? '권한이 없습니다' : ''} arrow componentsProps={{ tooltip: { sx: { fontSize: '13px' } } }}>
          <span>
            <RowActionButton
              tone="outline"
              variant="contained"
              size="small"
              onClick={() =>
                h.canPaymentDetailEdit &&
                // 행마다 자기 기록을 연다 — 환불행은 환불 정보, 결제행(환불된 주문의 원 결제 포함)·
                // 미수금결제행은 판매(해당 주문) 정보. 취소 행은 취소 기록(환불 정보 화면)을 보여준다.
                h.onClickDetail(
                  row,
                  isRefundRow(row) || row.paymentStatus === 'CANCELED' ? 'refund' : 'pay',
                )
              }
              disabled={!h.canPaymentDetailEdit}
              style={!h.canPaymentDetailEdit ? { opacity: 0.4, cursor: 'default' } : {}}>
              수정
            </RowActionButton>
          </span>
        </Tooltip>
      ),
    },
    { label: '메모', width: 200, align: 'left', render: row => row.memo },
  ],
};

// ── 양수금 내역(평면) config ──
// 양도양수 직접양도 수수료(member_order.ORDER_TYPE='TRANSFER_FEE')만 모아 보는 탭.
// 서버가 searchPayType=['transfer_fee'] 로 이미 필터·페이징한다(클라 filter 없음).
// 상품명(courseTicketName)은 원본 상품명(예 '3개월권', 없으면 '양도수수료'), 회원명은 양도자명.
export const TRANSFER_CONFIG = {
  tableWidth: '1364px',
  rowTone: salesRowTone,
  columns: [
    { label: '판매일', width: 160, render: row => row.paidDate },
    { label: '지점명', width: 200, nowrap: true, render: row => row.branchName },
    { label: '회원명', width: 88, render: memberLink },
    { label: '상품명', width: 240, render: row => row.courseTicketName },
    { label: '결제수단', width: 88, render: (row, h) => h.t(paymentMethodLabel(row.paymentMethod)) },
    { label: '양수금액', width: 140, align: 'right', render: row => num(row.finalPrice) },
    {
      label: '미수금',
      width: 140,
      align: 'right',
      tone: row => (row.outstandingAmount ? 'minus' : undefined),
      render: row => minusNum(row.outstandingAmount),
    },
    { label: '결제상태', width: 88, render: (row, h) => statusChip(row, h) },
    { label: '판매담당자', width: 100, render: row => <SellerNameCell managerName={row.managerName} fallback="" /> },
    { label: '메모', flex: true, align: 'left', render: row => row.memo },
  ],
};

// ── 전체 내역(평면) config ──
// 기존 MUI Table 기반 전체내역을 상품별내역과 동일한 PT* 디자인(SalesManagFlatTable)으로 통일한다.
// 'all' 조회 결과에는 payTypeName='양수금'(양도수수료) 행이 자동 포함된다(별도 호출 불필요).
//
// 변경점:
//  - 양도 수수료 컬럼: 환불 금액 뒤에 배치, 양수금 행이면 finalPrice(=수수료) 그 외 '-'.
//  - 환불 수단 → 환불 금액 → 양도 수수료 순서.
//  - 최종 결제 금액 헤더 폭 확대(140px)로 줄바꿈 깨짐 방지.
//  - 전화번호 컬럼 제거.
//  - 지점명 nowrap + 폭 확대(긴 지점명도 한 줄 유지).
//  - tableWidth = 전체 컬럼 폭 합(28컬럼=3384px). 합보다 작으면 끝 컬럼(판매담당자/메모/상세)이 표 밖으로 삐져나옴.
export const ALL_CONFIG = {
  tableWidth: '3384px',
  rowTone: salesRowTone,
  columns: [
    { label: '지점명', width: 220, nowrap: true, render: row => row.branchName },
    { label: '결제일', width: 130, render: (row, h) => paidDateWithChevron(row, h) },
    {
      label: '회원명',
      width: 100,
      render: (row, h) => (
        <MemberNameText clickable={!!row.memberId} onClick={() => row.memberId && h.onNavigateMember(row.memberId)}>
          {row.memberName || '(삭제회원)'}
        </MemberNameText>
      ),
    },
    {
      label: '신규/재등록',
      width: 112,
      render: row => (
        <span
          style={{
            color: Number(row.isNew) === 1 ? '#1f9d6b' : '#888',
            fontWeight: Number(row.isNew) === 1 ? 700 : 400,
          }}>
          {Number(row.isNew) === 1 ? '신규' : '재등록'}
        </span>
      ),
    },
    { label: '구분', width: 100, render: row => row.payTypeName },
    { label: '상품수량', width: 92, render: row => row.productQuantity ?? 1 },
    { label: '상품명', width: 200, align: 'left', render: row => row.courseTicketName },
    { label: '판매가격', width: 120, align: 'right', render: row => num(row.regularPrice) },
    {
      label: '할인금액',
      width: 120,
      align: 'right',
      tone: row => (row.discountPrice ? 'minus' : undefined),
      render: row => minusNum(row.discountPrice),
    },
    {
      label: '포인트사용',
      width: 120,
      align: 'right',
      tone: row => (row.usedPoint ? 'minus' : undefined),
      render: row => minusNum(row.usedPoint),
    },
    {
      label: '미수금',
      width: 120,
      align: 'right',
      tone: row => (row.outstandingAmount ? 'minus' : undefined),
      render: row => minusNum(row.outstandingAmount),
    },
    { label: '결제금액', width: 130, align: 'right', render: row => num(row.finalPrice) },
    // 환불 수단 → 환불 금액 → 양도 수수료 순서. 결제상태 바로 앞에 모아 둔다
    // (환불/양수 성격을 상태 라벨과 붙여서 읽게 하려는 배치).
    // 분할결제 환불은 갈매기(결제수단 셀)로 라인을 펼쳐 보므로 환불 수단은 '-' 로 둔다.
    {
      label: '환불 수단',
      width: 110,
      render: (row, h) =>
        row.refundMethod && row.refundMethod !== MIXED_METHOD ? h.t(paymentMethodLabel(row.refundMethod)) : '-',
    },
    {
      label: '환불 금액',
      width: 120,
      align: 'right',
      tone: row => (row.refundAmount ? 'refund' : undefined),
      render: row => (row.refundAmount ? refundText(row.refundAmount) : num(0)),
    },
    // 양도 수수료 — 양수금 행만 fee(=finalPrice) 표시, 그 외 '-'.
    {
      label: '양도 수수료',
      width: 120,
      align: 'right',
      render: row => (isTransferFeeRow(row) ? num(row.finalPrice) : '-'),
    },
    { label: '결제상태', width: 100, render: (row, h) => statusChip(row, h) },
    {
      label: '계약변경',
      width: 110,
      render: (row, h) =>
        row.paymentStatus === 'REFUNDED' ? (
          // 환불 행은 여기서 바로 환불취소(확인 다이얼로그+사유) — 환불 정보는 '상세' 수정에서 본다
          <RowActionButton tone="dark" variant="contained" size="small" onClick={() => h.onClickRefundReversal?.(row)}>
            환불취소
          </RowActionButton>
        ) : row.paymentStatus === 'CANCELED' ? (
          <RowActionButton tone="gray" variant="contained" size="small" onClick={() => h.onClickDetail(row, 'refund')}>
            보기
          </RowActionButton>
        ) : (
          <Tooltip title={!h.canContractEdit ? '권한이 없습니다' : ''} arrow componentsProps={{ tooltip: { sx: { fontSize: '13px' } } }}>
            <span>
              <RowActionButton
                tone="dark"
                variant="contained"
                size="small"
                onClick={() => h.canContractEdit && h.onClickCancel(row)}
                disabled={!h.canContractEdit}
                style={!h.canContractEdit ? { opacity: 0.4, cursor: 'default' } : {}}>
                계약변경
              </RowActionButton>
            </span>
          </Tooltip>
        ),
    },
    { label: '결제수단', width: 100, render: (row, h) => h.t(paymentMethodLabel(row.paymentMethod)) },
    { label: '현금', width: 110, align: 'right', render: row => methodCell(row, row.cashAmount, row.cashRefundAmount, isCashMethod) },
    {
      label: '계좌이체',
      width: 110,
      align: 'right',
      render: row => methodCell(row, row.transferAmount, row.transferRefundAmount, isTransferMethod),
    },
    { label: '카드', width: 110, align: 'right', render: row => methodCell(row, row.cardAmount, row.cardRefundAmount, isCardMethod) },
    { label: '카드사', width: 130, render: row => row.cardCompany || '-' },
    { label: '카드번호', width: 150, render: row => row.cardNumber || '-' },
    { label: '승인번호', width: 110, render: row => row.approvalNumber || '-' },
    { label: '결제경로', width: 100, render: row => row.paymentProviderName },
    { label: '판매담당자', width: 110, render: row => <SellerNameCell managerName={row.managerName} fallback="" /> },
    {
      label: '메모',
      width: 140,
      align: 'left',
      render: row => (row.memo?.length > 13 ? `${row.memo.slice(0, 13)}...` : row.memo),
    },
    {
      label: '상세',
      width: 90,
      render: (row, h) => (
        <Tooltip title={!h.canPaymentDetailEdit ? '권한이 없습니다' : ''} arrow componentsProps={{ tooltip: { sx: { fontSize: '13px' } } }}>
          <span>
            <RowActionButton
              tone="outline"
              variant="contained"
              size="small"
              onClick={() =>
                h.canPaymentDetailEdit &&
                // 행마다 자기 기록을 연다 — 환불행은 환불 정보, 결제행(환불된 주문의 원 결제 포함)·
                // 미수금결제행은 판매(해당 주문) 정보. 취소 행은 취소 기록(환불 정보 화면)을 보여준다.
                h.onClickDetail(
                  row,
                  isRefundRow(row) || row.paymentStatus === 'CANCELED' ? 'refund' : 'pay',
                )
              }
              disabled={!h.canPaymentDetailEdit}
              style={!h.canPaymentDetailEdit ? { opacity: 0.4, cursor: 'default' } : {}}>
              수정
            </RowActionButton>
          </span>
        </Tooltip>
      ),
    },
  ],
};
